import {
  ODSColumn,
  ODSIcon,
  ODSRow,
  ODSSupportMessage,
  ODSText,
} from "@/atoms";
import ODSCollapseDown from "@/atoms/ODSIcons/collapse-down/ODSCollapseDown";
import ODSCollapseUp from "@/atoms/ODSIcons/collapse-up/ODSCollapseUp";
import { useScopedColorScheme } from "@/utils";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  size as floatingSize,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from "@floating-ui/react";
import classNames from "classnames";
import React, {
  forwardRef,
  KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { ODSDropdownSelectProps } from "./ODSDropdownSelect.types";
import {
  generateTypeheadList,
  getIndexFromValueOrLabel,
  getLabel,
  getSelectedLabel,
  getSelectedLabelText,
} from "./ODSDropdownSelect.utils";
import ODSDropdownSelectMenuItem from "./ODSDropdownSelectMenuItem";

import "./css/index.css";

const ODSDropdownSelect = forwardRef<HTMLDivElement, ODSDropdownSelectProps>(
  (
    {
      className,
      items,
      defaultValue,
      value: valueProp,
      label,
      icon,
      mode = "standard",
      disabled = false,
      readonly = false,
      supportMessage,
      supportMessageProps,
      required = false,
      size = "large",
      onValueChange,
      onExpandedChange,
      ...rest
    },
    ref
  ) => {
    const uId = useId();
    const labelId = `ODSDropdownSelect-EyebrowLabel_${uId}`;
    const isValueControlled = valueProp !== undefined;
    const [expanded, setExpanded] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(
      getIndexFromValueOrLabel(items, defaultValue)
    );
    const selectedLabel = getSelectedLabel(items, label, selectedIndex);
    const toggleExpanded = (value: boolean) => {
      setExpanded(value);
      onExpandedChange?.(value);
    };

    const handleSelect = (index: number) => {
      if (items[index]?.disabled) return;
      const newIndex = selectedIndex === index ? null : index;
      if (!isValueControlled) setSelectedIndex(newIndex);
      const newValue = newIndex !== null ? items[newIndex]?.value : null;
      const newId = newIndex !== null ? items[newIndex]?.id : undefined;
      const newLabel = getSelectedLabelText(items, newIndex);

      onValueChange?.({
        value: newValue !== null ? newValue : undefined,
        id: newId,
        labelText: newLabel,
      });
      toggleExpanded(false);
    };

    const { refs, floatingStyles, context } = useFloating({
      strategy: "fixed",
      whileElementsMounted: autoUpdate,
      middleware: [
        offset(8),
        flip({ padding: 8 }),
        floatingSize({
          apply({ rects, elements, availableHeight }) {
            Object.assign(elements.floating.style, {
              maxHeight: `${availableHeight}px`,
              width: `${rects.reference.width}px`,
            });
          },
          padding: 8,
        }),
      ],
      open: expanded,
      onOpenChange: toggleExpanded,
      placement: "bottom-start",
    });

    const navigationListRef = useRef<(HTMLButtonElement | null)[]>([]);
    const click = useClick(context, { enabled: !(disabled || readonly) });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "listbox" });
    const listNav = useListNavigation(context, {
      listRef: navigationListRef,
      activeIndex,
      selectedIndex,
      onNavigate: (index) => setActiveIndex(index),
      focusItemOnHover: false,
      // Allow [disabled] and [aria-disabled="true"] to be keyboard-focusable.
      // https://floating-ui.com/docs/useListNavigation#disabledindices
      // This is a A11Y request for the Disabled Items
      disabledIndices: [],
    });
    const isTypingRef = useRef(false);
    const typeheadList = generateTypeheadList(items);
    const typeheadListRef = useRef(typeheadList);
    const typeahead = useTypeahead(context, {
      listRef: typeheadListRef,
      enabled: !(disabled || readonly),
      activeIndex,
      selectedIndex,
      onMatch: expanded ? setActiveIndex : setSelectedIndex,
      onTypingChange: (isTyping) => (isTypingRef.current = isTyping),
    });
    const { getReferenceProps, getFloatingProps, getItemProps } =
      useInteractions([dismiss, role, listNav, typeahead, click]);
    const { colorScheme, colorMode } = useScopedColorScheme(refs.reference, [
      expanded,
    ]);

    useEffect(() => {
      if (!disabled) return;
      setSelectedIndex(null);
      setExpanded(false);
    }, [disabled]);

    useEffect(() => {
      if (isValueControlled)
        setSelectedIndex(getIndexFromValueOrLabel(items, valueProp));
    }, [isValueControlled, items, valueProp]);

    const onDropdownItemClick = (index: number) => handleSelect(index);
    const onDropdownItemKeyDown = (event: KeyboardEvent, index: number) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSelect(index);
      }
      if (event.key === " " && !isTypingRef.current) {
        event.preventDefault();
        handleSelect(index);
      }
    };

    return (
      <>
        <div
          className={classNames(
            `ODSDropdownSelect`,
            `ODSDropdownSelect--mode-${mode}`,
            selectedIndex !== null && `ODSDropdownSelect--selected`,
            readonly && `ODSDropdownSelect--readonly`,
            disabled && `ODSDropdownSelect--disabled`,
            `ODSDropdownSelect--size-${size}`,
            className
          )}
          {...rest}
        >
          <div
            className="ODSDropdownSelect-InputField"
            {...getReferenceProps({
              ref: refs.setReference,
              "aria-label":
                selectedIndex !== null
                  ? `${label} ${selectedLabel}`
                  : selectedLabel,
              "aria-controls": `listbox-${uId}`,
              "aria-description": supportMessage ? supportMessage : undefined,
              "aria-disabled": disabled,
              "aria-required": required,
            })}
          >
            <ODSColumn
              className="ODSDropdownSelect-Content"
              ref={ref}
              tabIndex={disabled ? -1 : 0}
            >
              {selectedIndex !== null && !disabled ? (
                <ODSText
                  as="label"
                  id={labelId}
                  className="ODSDropdownSelect-EyebrowLabel"
                >
                  {getLabel(label, required)}
                </ODSText>
              ) : null}
              <ODSRow className="ODSDropdownSelect-Input">
                {icon ? (
                  <ODSIcon
                    name={icon}
                    className="ODSDropdownSelect-Icon"
                    tabIndex={undefined}
                    aria-hidden="true"
                  />
                ) : null}
                <ODSText as="span" className="ODSDropdownSelect-Value">
                  {getLabel(
                    selectedLabel,
                    required && selectedIndex === null && !disabled
                  )}
                </ODSText>
              </ODSRow>
            </ODSColumn>
            <ODSRow className="ODSDropdownSelect-ExpandAndCollapseIcon">
              {!expanded ? (
                <ODSCollapseDown
                  className="ODSDropdownSelect-CollapseDown"
                  tabIndex={undefined}
                  aria-hidden="true"
                />
              ) : (
                <ODSCollapseUp
                  className="ODSDropdownSelect-CollapseUp"
                  tabIndex={undefined}
                  aria-hidden="true"
                />
              )}
            </ODSRow>
          </div>
          {supportMessage && (
            <ODSRow
              className="ODSDropdownSelect-SupportText"
              aria-hidden="true"
              tabIndex={-1}
            >
              {supportMessage && mode !== "standard" && (
                <ODSSupportMessage
                  {...supportMessageProps}
                  disabled={disabled}
                  helperText={supportMessage}
                  className={classNames(
                    "ODSDropdownSelect-OdsSupportMessage",
                    supportMessageProps?.className
                  )}
                  mode={mode === "error" ? "error" : "informative"}
                  aria-hidden="true"
                />
              )}
            </ODSRow>
          )}
        </div>
        {expanded ? (
          <FloatingPortal>
            <FloatingFocusManager context={context} modal={false}>
              <div
                className={classNames(
                  "ODSDropdownSelectMenu",
                  `ODSDropdownSelectMenu--size-${size}`
                )}
                data-scheme={colorScheme}
                data-mode={colorMode}
                {...getFloatingProps({
                  ref: refs.setFloating,
                  style: floatingStyles,
                })}
                id={`listbox-${uId}`}
              >
                {items?.map((item, i) => {
                  const { labelText, ...rest } = item;
                  return (
                    <ODSDropdownSelectMenuItem
                      key={`ODSDropdownSelectMenuItem_${i}`}
                      ref={(node) => {
                        if (node) navigationListRef.current[i] = node;
                      }}
                      labelText={labelText}
                      tabIndex={activeIndex === i ? 0 : -1}
                      selected={i === selectedIndex}
                      {...getItemProps({
                        onClick: () => onDropdownItemClick(i),
                        onKeyDown: (e) => onDropdownItemKeyDown(e, i),
                      })}
                      {...rest}
                    />
                  );
                })}
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        ) : null}
      </>
    );
  }
);
ODSDropdownSelect.displayName = "ODSDropdownSelect";

export default ODSDropdownSelect;
